import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Select from '../../molecules/Select';
import PropertyControlledComponent from '../../molecules/PropertyControlledComponent';
import Content from '../../atoms/Content';
import PaginationButton from './_paginationButton';
import PaginationLink from './_paginationLink';
import './pagination.scss';
var getStartPage = function getStartPage(_ref) {
  var pagerSize = _ref.pagerSize,
    currentPage = _ref.currentPage;
  var maxBufferPages = Math.floor(pagerSize / 2);
  var bufferPagesFromStart = currentPage < maxBufferPages ? currentPage - 1 : maxBufferPages;
  if (currentPage === bufferPagesFromStart) {
    return 1;
  }
  return currentPage > bufferPagesFromStart ? currentPage - bufferPagesFromStart : currentPage;
};
var getPagerData = function getPagerData(_ref2) {
  var totalPages = _ref2.totalPages,
    currentPage = _ref2.currentPage,
    pagerSize = _ref2.pagerSize;
  var pageRange = pagerSize > totalPages ? totalPages : pagerSize;
  var canDisplayFirstLast = totalPages > pageRange;
  var startPage = getStartPage({
    pagerSize: pagerSize,
    currentPage: currentPage
  });
  var endPage = startPage + (pageRange - 1);
  if (endPage > totalPages) {
    var numberOfBufferPages = endPage - totalPages;
    startPage -= numberOfBufferPages;
    endPage = totalPages;
  }
  var pagesToBeDisplayed = Array.from({
    length: endPage + 1 - startPage
  }).map(function (val, index) {
    return index + startPage;
  });
  return {
    pagesToBeDisplayed: pagesToBeDisplayed,
    canDisplayFirstLast: canDisplayFirstLast
  };
};
var getPaginationButtonProps = function getPaginationButtonProps(_ref3) {
  var currentPage = _ref3.currentPage,
    totalPages = _ref3.totalPages,
    isLoading = _ref3.isLoading;
  var isFirstPrevButtonsDisabled = currentPage === 1 || isLoading;
  var isLastNextButtonsDisabled = currentPage === totalPages || isLoading || totalPages === 0;
  return {
    isFirstPrevButtonsDisabled: isFirstPrevButtonsDisabled,
    isLastNextButtonsDisabled: isLastNextButtonsDisabled
  };
};
var Pagination = function Pagination(props) {
  var totalPages = props.totalPages,
    currentPage = props.currentPage,
    pagerSize = props.pagerSize,
    initialFetchDone = props.initialFetchDone,
    displayFirstLast = props.displayFirstLast,
    isLoading = props.isLoading,
    changePage = props.changePage,
    resultsPerPage = props.resultsPerPage,
    canSetResultsPerPage = props.canSetResultsPerPage,
    setResultsPerPage = props.setResultsPerPage,
    resultsPerPageOptions = props.resultsPerPageOptions,
    fixedPagination = props.fixedPagination,
    className = props.className;
  var _getPagerData = getPagerData({
      totalPages: totalPages,
      currentPage: currentPage,
      pagerSize: pagerSize
    }),
    pagesToBeDisplayed = _getPagerData.pagesToBeDisplayed,
    canDisplayFirstLast = _getPagerData.canDisplayFirstLast;
  var _getPaginationButtonP = getPaginationButtonProps({
      currentPage: currentPage,
      totalPages: totalPages,
      isLoading: isLoading
    }),
    isFirstPrevButtonsDisabled = _getPaginationButtonP.isFirstPrevButtonsDisabled,
    isLastNextButtonsDisabled = _getPaginationButtonP.isLastNextButtonsDisabled;
  var shouldDisplayFirstLast = displayFirstLast && canDisplayFirstLast;
  if (!initialFetchDone) {
    return /*#__PURE__*/React.createElement("div", {
      className: "pagination is-loading"
    });
  }
  var resultsPerPageSelectOptions = resultsPerPageOptions.map(function (resultsPerPageOption) {
    return {
      label: resultsPerPageOption,
      value: resultsPerPageOption
    };
  });
  return /*#__PURE__*/React.createElement("div", {
    className: cx('pagination', {
      'stick-pagination-component': fixedPagination
    }, className)
  }, /*#__PURE__*/React.createElement(PropertyControlledComponent, {
    controllerProperty: canSetResultsPerPage
  }, /*#__PURE__*/React.createElement("div", {
    className: "pagination__results-per-page"
  }, /*#__PURE__*/React.createElement(Select, {
    config: {
      label: 'label',
      value: 'value'
    },
    onChange: setResultsPerPage,
    value: resultsPerPage,
    options: resultsPerPageSelectOptions,
    defaultValue: resultsPerPage,
    changeSuffixIconColor: true
  }), /*#__PURE__*/React.createElement(Content, {
    className: "pagination__results-per-page__title"
  }, __('Results Per Page')), /*#__PURE__*/React.createElement("div", {
    className: "pagination__results-per-page__divider"
  }))), /*#__PURE__*/React.createElement(PropertyControlledComponent, {
    controllerProperty: shouldDisplayFirstLast
  }, /*#__PURE__*/React.createElement(PaginationButton, {
    changePage: changePage,
    pageNumber: 1,
    isDisabled: isFirstPrevButtonsDisabled,
    buttonText: __('First'),
    isJumpButton: true,
    hasLeftIcon: true
  })), /*#__PURE__*/React.createElement(PaginationButton, {
    changePage: changePage,
    pageNumber: currentPage - 1,
    isDisabled: isFirstPrevButtonsDisabled,
    buttonText: __('Prev'),
    isJumpButton: true,
    hasLeftIcon: true
  }), pagesToBeDisplayed.map(function (page) {
    return /*#__PURE__*/React.createElement(PaginationLink, {
      changePage: changePage,
      pageNumber: page,
      isDisabled: isLoading,
      isActive: currentPage === page,
      key: page
    });
  }), /*#__PURE__*/React.createElement(PaginationButton, {
    changePage: changePage,
    pageNumber: currentPage + 1,
    isDisabled: isLastNextButtonsDisabled,
    buttonText: __('Next'),
    isJumpButton: true,
    hasLeftIcon: false
  }), /*#__PURE__*/React.createElement(PropertyControlledComponent, {
    controllerProperty: shouldDisplayFirstLast
  }, /*#__PURE__*/React.createElement(PaginationButton, {
    changePage: changePage,
    pageNumber: totalPages,
    isDisabled: isLastNextButtonsDisabled,
    buttonText: __('Last'),
    isJumpButton: true,
    hasLeftIcon: false
  })));
};
Pagination.defaultProps = {
  totalPages: 8,
  currentPage: 1,
  pagerSize: 8,
  initialFetchDone: true,
  isLoading: false,
  displayFirstLast: false,
  canSetResultsPerPage: true,
  resultsPerPage: 10,
  resultsPerPageOptions: [10, 11, 12, 13, 14, 15],
  fixedPagination: false,
  className: PropTypes.string
};
Pagination.propTypes = {
  totalPages: PropTypes.number,
  currentPage: PropTypes.number,
  pagerSize: PropTypes.number,
  initialFetchDone: PropTypes.bool,
  isLoading: PropTypes.bool,
  displayFirstLast: PropTypes.bool,
  resultsPerPage: PropTypes.number,
  canSetResultsPerPage: PropTypes.bool,
  changePage: PropTypes.func.isRequired,
  setResultsPerPage: PropTypes.func.isRequired,
  resultsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  fixedPagination: PropTypes.bool,
  className: ''
};
export default Pagination;